import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { lower } from "../../lib/strings";
import { toast } from "sonner";

const BUCKETS = [
  { key: "opening_soon", label: "opening soon" },
  { key: "closing_soon", label: "closing soon" },
  { key: "recently_closed", label: "recently closed" },
  { key: "missing_poster", label: "missing poster" },
  { key: "missing_description", label: "missing description" },
  { key: "incomplete_credits", label: "incomplete credits" },
  { key: "needs_fact_check", label: "needs fact check" },
];

// Admin upkeep dashboard — buckets shows by curatorial state to make catalogue
// maintenance scannable. Pure read view. Each card links to that show's
// detail page or to suggest-credits, depending on what's needed.
export default function AdminUpkeep() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState("opening_soon");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/shows/upkeep/dashboard");
        setData(data);
      } catch (err) {
        toast(formatApiError(err));
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  if (!loaded) {
    return <p className="px-5 py-10 font-mono text-xs text-white/40">loading upkeep...</p>;
  }
  if (!data) {
    return <p className="px-5 py-10 font-mono text-xs text-white/40">upkeep unavailable.</p>;
  }

  const items = data.buckets[active] || [];

  return (
    <section className="px-5 py-6" data-testid="admin-upkeep-page">
      <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">
        upkeep · today {data.today}
      </h2>

      <div className="flex gap-2 flex-wrap mb-4">
        {BUCKETS.map((b) => {
          const n = data.counts[b.key] ?? 0;
          const isActive = active === b.key;
          return (
            <button
              key={b.key}
              data-testid={`upkeep-bucket-${b.key}`}
              onClick={() => setActive(b.key)}
              className={`font-mono text-[10px] uppercase tracking-wider border-2 px-2.5 py-1.5 transition-colors flex items-center gap-1.5 ${
                isActive
                  ? "border-acid bg-acid text-black"
                  : "border-white/30 text-white/70 hover:border-white"
              }`}
            >
              <span>{b.label}</span>
              <span className={`tabular-nums ${isActive ? "text-black" : "text-acid"}`}>
                {n}
              </span>
            </button>
          );
        })}
      </div>

      {items.length === 0 ? (
        <p className="font-mono text-xs text-white/40">nothing in this bucket.</p>
      ) : (
        <ul className="space-y-2" data-testid={`upkeep-list-${active}`}>
          {items.map((s) => (
            <li
              key={s.id}
              data-testid={`upkeep-row-${s.id}`}
              className="border border-white/10 px-3 py-2 flex items-baseline gap-3 flex-wrap"
            >
              <Link
                to={`/show/${s.id}`}
                className="font-display text-base hover:text-acid flex-1 min-w-0 truncate"
              >
                {lower(s.title)}
              </Link>
              <span className="font-mono text-[10px] text-white/50 truncate">
                {lower(s.company || s.theatre || "")}
              </span>
              <span className="font-mono text-[10px] text-white/40 shrink-0">
                {s.startDate || "—"} → {s.endDate || "—"}
              </span>
              {active === "incomplete_credits" && (
                <Link
                  to={`/show/${s.id}/suggest-credits`}
                  className="font-mono text-[10px] text-acid underline shrink-0"
                >
                  add credits
                </Link>
              )}
              {active === "needs_fact_check" && s.sourceUrl && (
                <a
                  href={s.sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="font-mono text-[10px] text-acid underline shrink-0"
                >
                  source
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
